import {
  addBreadcrumb,
  captureMessage,
  logger,
  setContext,
  setTag,
} from "@/sentry";

const getPermissionState = async (
  name: string,
  devices: MediaDeviceInfo[],
): Promise<{ state: PermissionState; permissionsApiSupported: boolean }> => {
  try {
    const result = await navigator.permissions.query({
      name: name as PermissionName,
    });
    return { state: result.state, permissionsApiSupported: true };
  } catch {
    // Webkit (iOS) doesn't support Permissions API for media devices
    if (devices.length === 0)
      return { state: "denied", permissionsApiSupported: false };
    const hasLabels = devices.some((d) => d.label !== "");
    // Browsers only show labels when permissions is granted
    return {
      state: hasLabels ? "granted" : "denied",
      permissionsApiSupported: false,
    };
  }
};

export const logMediaPermissions = async () => {
  // No-op if navigator is missing
  if (!navigator) {
    return;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((d) => d.kind === "audioinput");
    const videoInputs = devices.filter((d) => d.kind === "videoinput");

    const [mic, camera] = await Promise.all([
      getPermissionState("microphone", audioInputs),
      getPermissionState("camera", videoInputs),
    ]);

    const micPermission = mic.state;
    const cameraPermission = camera.state;
    const permissionsApiSupported = mic.permissionsApiSupported;

    const permissionsData = {
      microphone: {
        permission: micPermission,
        hasDevice: audioInputs.length > 0,
        deviceCount: audioInputs.length,
      },
      camera: {
        permission: cameraPermission,
        hasDevice: videoInputs.length > 0,
        deviceCount: videoInputs.length,
      },
      permissionsApiSupported,
    };

    setContext("media-permissions", permissionsData);

    setTag("media_permission_state", micPermission);
    setTag("camera_permission_state", cameraPermission);

    addBreadcrumb({
      category: "media-permissions",
      message: `Media permissions state — mic=${micPermission} (${audioInputs.length} devices), camera=${cameraPermission} (${videoInputs.length} devices), permissionsApiSupported=${permissionsApiSupported}`,
      level: "info",
    });

    const micDenied = micPermission === "denied";
    const cameraDenied = cameraPermission === "denied";
    const micNoDevice = audioInputs.length === 0 && !micDenied;
    const cameraNoDevice = videoInputs.length === 0 && !cameraDenied;

    const hasIssues =
      micDenied || cameraDenied || micNoDevice || cameraNoDevice;

    if (hasIssues) {
      captureMessage("Media permissions issue detected", {
        level: "warning",
        extra: {
          category: "media-permissions",
          ...permissionsData,
        },
      });
    }
  } catch (error) {
    logger.error("Media permissions check failed", { error });
  }
};
